import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import has from '@dojo/framework/core/has';

import * as URI from 'uri-js';

import { SlidingPane, Align } from '@holmescorp/base-widgets/sliding-pane';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { publish, subscribe } from 'pub-sub';
import { createNlsMiddleware, lccstate, registry } from 'lcc-state';
import { user_logout } from 'logout';
import { QuizChoice } from '../widgets/quiz-choice';
import { convertToBoolean, delay } from '@holmescorp/common';
import { viewport } from '../support/viewport';
import { ExamCountdown } from '@holmescorp/exam-countdown';
import { WhereYouLeftOffSetting } from '../widgets/where-you-left-off-setting';

import css from '../css/heading.m.css';

interface SettingsPaneIcache {
	open: boolean;
	aboutToOpen: boolean;
	hideCountdown: boolean;
}

interface SettingsPaneProperties {
	onClose?: () => void;
}

const icache = createICacheMiddleware<SettingsPaneIcache>();
const nls = createNlsMiddleware('settings');
const factory = create({
	icache,
	theme,
	nls,
	lccstate
}).properties<SettingsPaneProperties>();

export const SettingsPane = factory(({ properties, middleware: { icache, theme, nls, lccstate } }) => {
	const { onClose } = properties();
	icache.getOrSet('aboutToOpen', false);
	const open = icache.getOrSet('open', () => {
		subscribe('settings/open', async () => {
			icache.set('aboutToOpen', true);
			await delay(200);
			icache.set('open', true);
		});
		subscribe('close-all/panes', () => {
			icache.set('open', false);
		});
		return false;
	});

	const {
		messages: {
			goto_prc = '[settings] goto_prc',
			goto_lac = '[settings] goto_lac',
			goto_am = '[settings] goto_am',
			goto_welcome = '[settings] goto_welcome',
			goto_help = '[settings] goto_help',
			help_url,
			my_profile = '[settings] my_profile',
			log_out = '[settings] log_out',
			header_menu = '[settings] header_menu',
			include_exam_countdown = 'false',
			include_quiz_choice = 'true'
		}
	} = nls();

	const quizpatterns = lccstate.get(lccstate.path('quizpatterns'));
	const needsQuizChoice =
		convertToBoolean(include_quiz_choice) && quizpatterns && Object.keys(quizpatterns).length > 1;
	const themedCss = theme.classes(css);

	const nonLearner = Boolean(has('non-learner-role'));
	const useWYLO = has('use-wylo');
	const { vw } = viewport();
	const xqa = `settings-pane-${open}`;

	return w(
		SlidingPane,
		{
			xqa,
			key: 'settings-pane',
			open,
			align: 'right' as Align,
			underlay: true,
			title: header_menu,
			width: Math.min(vw, 1024),
			onRequestClose: () => {
				icache.set('open', false);
				icache.set('aboutToOpen', async () => {
					await delay(500);
					return true;
				});
				publish('settings/close');
				onClose && onClose();
			}
		},
		[
			needsQuizChoice ? w(QuizChoice, {}) : null,
			include_exam_countdown === 'true' ? w(ExamCountdown, {}) : null,
			useWYLO ? w(WhereYouLeftOffSetting, {}) : null,
			v('ul', { classes: [themedCss.hamburgerMenu] }, [
				nonLearner
					? v(
							'li',
							{
								key: '3',
								xqa: 'setting-prc-link',
								tabIndex: 0,
								classes: [themedCss.hamburgerMenuItem],
								onclick: (event) => {
									event && event.stopPropagation();
									const parts = URI.parse(window.hcstatic.portal_url!);
									parts.path = '';
									parts.host = parts.host!.split('.').slice(1).join('.');
									window.location.replace(URI.serialize(parts));
								}
							},
							[goto_prc]
							// tslint:disable-next-line:indent
					  )
					: null,
				nonLearner
					? v(
							'li',
							{
								key: '4',
								xqa: 'setting-lac-link',
								tabIndex: 0,
								classes: [themedCss.hamburgerMenuItem],
								onclick: (event) => {
									event && event.stopPropagation();
									window.location.replace('/lac');
								}
							},
							[goto_lac]
							// tslint:disable-next-line:indent
					  )
					: null,
				nonLearner
					? v(
							'li',
							{
								key: '5',
								xqa: 'setting-am-link',
								tabIndex: 0,
								classes: [themedCss.hamburgerMenuItem],
								onclick: (event) => {
									event && event.stopPropagation();
									window.location.replace('/am');
								}
							},
							[goto_am]
							// tslint:disable-next-line:indent
					  )
					: null,
				v(
					'li',
					{
						key: '7',
						xqa: 'setting-welcome-link',
						tabIndex: 0,
						classes: [themedCss.hamburgerMenuItem],
						onclick: (event) => {
							event && event.stopPropagation();
							window.location.replace('/am/@@welcome');
						}
					},
					[goto_welcome]
				),
				v(
					'li',
					{
						key: '8',
						xqa: 'setting-profile-link',
						tabIndex: 0,
						classes: [themedCss.hamburgerMenuItem],
						onclick: (event) => {
							event && event.stopPropagation();
							window.location.replace('/am/@@profile');
						}
					},
					[my_profile]
				),
				v(
					'li',
					{
						key: '9',
						xqa: 'setting-help-link',
						tabIndex: 0,
						classes: [themedCss.hamburgerMenuItem],
						onclick: (event) => {
							event && event.stopPropagation();
							help_url && window.open(help_url);
						}
					},
					[goto_help]
				),
				v(
					'li',
					{
						key: '10',
						xqa: 'setting-logout-link',
						tabIndex: 0,
						classes: [themedCss.hamburgerMenuItem],
						onclick: (event) => {
							event && event.stopPropagation();
							user_logout();
						}
					},
					[log_out]
				)
			])
		]
	);
});

registry.define('SettingsPane', SettingsPane);
